import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import RangeSlider from 'react-range-slider-input';

import './RangeSlider.css';
import 'react-range-slider-input/dist/style.css';

export interface RangeSliderProps {
	name: string;
	className?: string;
	label?: string | React.ReactElement;
	min: number; // minimum selectable value
	max: number; // maximum selectable value
	step?: number;
	initialValue?: [number, number]; // [min, max]: initial slider min and max values
	fieldValue: [number, number];
	handleChange?: (newValue: [number, number]) => void | undefined;
	surfaceError?: boolean; // if true, invalid form values will still be set, this should be handled in the form validation
}

export const RangeSliderV2 = (props: RangeSliderProps) => {
	const {
		name,
		className = '',
		min,
		max,
		step = 1,
		initialValue,
		fieldValue,
		handleChange,
		surfaceError = false,
	} = props;
	const currentValue = initialValue;
	const initialValueMin = currentValue && currentValue[0] >= min ? currentValue[0] : min;
	const initialValueMax = currentValue && currentValue[1] <= max ? currentValue[1] : max;
	const [value, setValue] = useState([initialValueMin, initialValueMax]);
	const [minInputValue, setMinInputValue] = useState(`${initialValueMin}`);
	const [maxInputValue, setMaxInputValue] = useState(`${initialValueMax}`);
	const [inputError, setInputError] = useState(false);
	const [formValueHandlingChange, setFormValueHandlingChange] = useState(false);
	const [valuesInitialised, setValuesInitialised] = useState(false);
	const valueTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const maxStrLength = `${max}`.length;

	useEffect(() => {
		if (JSON.stringify(value) !== JSON.stringify(fieldValue)) {
			// if surfaceError is true, we want to allow the slider value to be set outside of the min and max values to reflect the invalid form state,
			// otherwise we want to clamp it within the min and max values
			const newValue: [number, number] = surfaceError
				? [value[0], value[1]]
				: [
						value && value[0] > min ? value[0] : min,
						value && value[1] < max ? value[1] : max,
					];
			setFormValueHandlingChange(true);
			handleChange && handleChange(newValue);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, max, min, handleChange]);

	useEffect(() => {
		if (formValueHandlingChange) {
			// if the form value is changing, we don't want to update the slider value to reflect that change, as it will cause an infinite loop
			setFormValueHandlingChange(false);
			return;
		} else {
			if (
				fieldValue &&
				fieldValue.length > 0 &&
				JSON.stringify(value) !== JSON.stringify(fieldValue)
			) {
				// if fieldValue changes externally, we want to update the slider value to reflect that change
				const newValue: [number, number] = [fieldValue[0], fieldValue[1]];
				sliderChange(newValue);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fieldValue]);

	const sliderChange = (newValue: number[]) => {
		// inputs and slider values are different in order to handle invalid inputs without breaking the slider
		setMinInputValue(`${newValue[0]}`);
		setMaxInputValue(`${newValue[1]}`);
		setInputError(false);
		valueTimer.current && clearTimeout(valueTimer.current);
		setValue(newValue);
	};

	const inputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const targetName = e.target.name;
		const newVal = e.target.value.replace(/\D/g, '');
		setInputError(false);
		let newMin = value[0];
		let newMax = value[1];
		if (targetName.includes('min')) {
			setMinInputValue(newVal);
			const minInt = parseInt(newVal);
			// If surfaceError is true and the input is empty, set newMin to min-1 to trigger the error state; otherwise, default to min
			// we set to min-1 rather than just leaving it empty because the form value requires a number value, and this allows us to show the error state without breaking the functionality
			newMin = !isNaN(minInt) ? minInt : surfaceError ? min - 1 : min;
			if (newVal.length === 0 || newMin < min || newMin > newMax) {
				setInputError(true);
			}
		} else if (targetName.includes('max')) {
			setMaxInputValue(newVal);
			// If surfaceError is true and the input is empty, set newMax to max+1 to trigger the error state; otherwise, default to max
			// we set to max+1 rather than just leaving it empty because the form value requires a number value, and this allows us to show the error state without breaking the functionality
			newMax = newVal === '' ? (surfaceError ? max + 1 : max) : parseInt(newVal);
			if (newVal.length === 0 || newMax < newMin) {
				setInputError(true);
			}
			// If the entered max value exceeds the allowed maximum, clamp it to max and update the input field
			// don't want to do this if surfaceError is true and the input is empty because we need the invalid value
			if (newMax > max && !(surfaceError && newVal === '')) {
				newMax = max;
				setMaxInputValue(`${newMax}`);
			}
		}
		// add a small delay to prevent the slider from moving if the user is the middle of typing
		valueTimer.current && clearTimeout(valueTimer.current);
		valueTimer.current = setTimeout(() => setValue([newMin, newMax]), 500);
	};

	const computedValue = useMemo((): [number, number] => {
		// generates the value to be shown on the slider
		// the slider will break if the value is out of bounds, so we need to make sure to clamp it within the min and max values, even if surfaceError is true
		// note, this doesn't actually change the form state value only computes the value to be shown on the slider
		let minVal = parseInt(`${value[0]}`) || min;
		if (`${value[0]}`.length === 0 || minVal < min) minVal = min;
		let maxVal = parseInt(`${value[1]}`) || max;
		if (`${value[1]}`.length === 0 || maxVal > max) maxVal = max;
		return [minVal, maxVal];
	}, [value, min, max]);

	useEffect(() => {
		// Component has mounted
		// This fixes a render issue with the RangeSlider component where it doesn't render correctly on the first render.
		setValuesInitialised(true);
	}, []);

	return (
		<div className={`flex flex-row mt-2 ${className}`}>
			<div>
				<input
					id={name}
					type='text'
					name='input-min'
					value={minInputValue}
					onChange={inputChange}
					inputMode='numeric'
					maxLength={maxStrLength}
					className={clsx(
						inputError
							? 'text-error border-error placeholder:text-error focus:ring-error focus:border-error'
							: 'text-secondaryTextDark border-secondaryTextDark focus:ring-primaryBrand focus:border-primaryBrand',
						'block w-10 p-1 rounded h-8 border-1  text-body-sm font-normal  placeholder:text-base placeholder:text-disabledText text-center'
					)}
				/>
			</div>
			<div className='flex-grow px-2 py-3' aria-hidden='true'>
				{valuesInitialised && (
					<RangeSlider
						min={min}
						max={max}
						step={step}
						onInput={sliderChange}
						value={computedValue}
					/>
				)}
			</div>
			<div className='flex'>
				<input
					type='text'
					name='input-max'
					value={maxInputValue}
					onChange={inputChange}
					inputMode='numeric'
					maxLength={maxStrLength}
					className={clsx(
						inputError
							? 'text-error border-error placeholder:text-error focus:ring-error focus:border-error'
							: 'text-secondaryTextDark border-secondaryTextDark focus:ring-primaryBrand focus:border-primaryBrand',
						'block w-10 p-1 rounded h-8 border-1  text-body-sm font-normal  placeholder:text-base placeholder:text-disabledText text-center'
					)}
				/>
			</div>
		</div>
	);
};
